import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import toast, { Toaster, ToastBar } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { homeForRole } from './utils/homeForRole';
import hasRole from './utils/hasRole';
import { configAPI } from './services/api';

// Layouts
import AdminLayout from './components/common/AdminLayout';
import AdminAppLayout from './components/common/AdminAppLayout';
import CustomerLayout from './components/common/CustomerLayout';
import AgentAppLayout from './components/agent/AgentAppLayout';
import TechnicalLayout from './components/technical/TechnicalLayout';
import { useViewMode } from './hooks/useViewMode';

// Admin / Staff Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import InsightsPage from './pages/InsightsPage';
import CustomersPage from './pages/CustomersPage';
import StbInventoryPage from './pages/StbInventoryPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import RemountOnRouteParam from './components/common/RemountOnRouteParam';
import AddCustomerPage from './pages/AddCustomerPage';
import SearchPage from './pages/SearchPage';
import PaymentsPage from './pages/PaymentsPage';
import OnlinePaymentsPage from './pages/OnlinePaymentsPage';
import AdvanceReceiptsPage from './pages/AdvanceReceiptsPage';
import RenewalsPage from './pages/RenewalsPage';
import ComplaintsPage from './pages/ComplaintsPage';
import ComplaintDetailPage from './pages/ComplaintDetailPage';
import ServiceRequestsPage from './pages/ServiceRequestsPage';
import AdminLookupPage from './pages/AdminLookupPage';
import ConnectionRequestsPage from './pages/ConnectionRequestsPage';
import ReconnectionLeadsPage from './pages/ReconnectionLeadsPage';
import DiscontinuedCatvPage from './pages/DiscontinuedCatvPage';
import DevicesPage from './pages/DevicesPage';
import PlansPage from './pages/PlansPage';
import ReportsPage from './pages/ReportsPage';
import CollectionSheetPage from './pages/CollectionSheetPage';
import UsersPage from './pages/UsersPage';
import SiteRolesPage from './pages/SiteRolesPage';
import MoneyManagerPage from './pages/MoneyManagerPage';
import NotesPage from './pages/NotesPage';
import GstBillingPage from './pages/gst/GstBillingPage';
import ClaudeChatPage from './pages/claudechat/ClaudeChatPage';
import AgentCollectionPage from './pages/AgentCollectionPage';
import AgentDetailPage from './pages/AgentDetailPage';
import ImportPage from './pages/ImportPage';
import HistoryImportPage from './pages/HistoryImportPage';
import BalanceUpdatePage from './pages/BalanceUpdatePage';
import NeedsPlanPage from './pages/NeedsPlanPage';
import ExportContactsPage from './pages/ExportContactsPage';
import SyncImportPage from './pages/SyncImportPage';
import CustomerLinkingPage from './pages/CustomerLinkingPage';
import OLTPage from './pages/OLTPage';
import OltInfoV2Page from './pages/OltInfoV2Page';
import TestingPage from './pages/TestingPage';
import AcsPage from './pages/AcsPage';
import WhatsAppPage from './pages/WhatsAppPage';
import WhatsAppInboxPage from './pages/WhatsAppInboxPage';
import KapsoPage from './pages/KapsoPage';
import FiberMapPage from './pages/FiberMapPage';
import SettingsPage from './pages/SettingsPage';
import IMBSPage from './pages/IMBSPage';
import GtplSaathiTicketPage from './pages/GtplSaathiTicketPage';
import GtplHomePage, { GtplShell } from './pages/GtplHomePage';
import GtplSaathiSubscriberPage from './pages/GtplSaathiSubscriberPage';
import GtplSaathiMultiRenewPage from './pages/GtplSaathiMultiRenewPage';
import GtplSaathiModifyPage from './pages/GtplSaathiModifyPage';
import GtplSaathiHardwareReplacementPage from './pages/GtplSaathiHardwareReplacementPage';
import GtplVsCrmPage from './pages/GtplVsCrmPage';
import GtplExpiryCheckPage from './pages/GtplExpiryCheckPage';
import GtplBoxStockPage from './pages/GtplBoxStockPage';
import GtplOrphanAccountsPage from './pages/GtplOrphanAccountsPage';
import GtplStbMismatchPage from './pages/GtplStbMismatchPage';
import Gtpl90DaysPage from './pages/Gtpl90DaysPage';
import GtplAlacarteBulkPage from './pages/GtplAlacarteBulkPage';
import GtplBulkChangeOrderPage from './pages/GtplBulkChangeOrderPage';
import GtplSaathiSettingsPage from './pages/GtplSaathiSettingsPage';
import GtplRetryQueuePage from './pages/GtplRetryQueuePage';
import GtplActionLogPage from './pages/GtplActionLogPage';
import MessageCenterPage from './pages/MessageCenterPage';
import MessagingApiPage from './pages/MessagingApiPage';
import ChangeLedgerPage from './pages/ChangeLedgerPage';
import PaymentScreenshotsPage from './pages/PaymentScreenshotsPage';
import RecycleBinPage from './pages/RecycleBinPage';
import AdsPage from './pages/AdsPage';

// Customer Portal Pages
import NewConnectionRequestPage from './pages/customer/NewConnectionRequestPage';
import PrivacyPolicyPage from './pages/customer/PrivacyPolicyPage';
import RefundPolicyPage from './pages/customer/RefundPolicyPage';
import TermsPage from './pages/customer/TermsPage';
import PayBillPage from './pages/public/PayBillPage';
import PayBillResultPage from './pages/public/PayBillResultPage';
import CustomerDashboard from './pages/customer/CustomerDashboard';
import CustomerComplaints from './pages/customer/CustomerComplaints';
import CustomerRequests from './pages/customer/CustomerRequests';
import CustomerNotifications from './pages/customer/CustomerNotifications';
import CustomerRefer from './pages/customer/CustomerRefer';
import CustomerPayments from './pages/customer/CustomerPayments';
import CustomerProfile from './pages/customer/CustomerProfile';
import CustomerPlansPage from './pages/customer/CustomerPlansPage';
import CustomerRenewPage from './pages/customer/CustomerRenewPage';
import CustomerSpeedTest from './pages/customer/CustomerSpeedTest';
import CustomerWifiSettings from './pages/customer/CustomerWifiSettings';
import CustomerUsageDetails from './pages/customer/CustomerUsageDetails';

// Agent App Pages (mobile, cable-TV-only collection interface)
import AgentCustomerListPage from './pages/agent/AgentCustomerListPage';
import AgentCustomerDetailPage from './pages/agent/AgentCustomerDetailPage';
import AgentCollectPaymentPage from './pages/agent/AgentCollectPaymentPage';
import OnlineSubscribersPage from './pages/OnlineSubscribersPage';
import AgentCollectBalancePage from './pages/agent/AgentCollectBalancePage';
import AgentReceiptPage from './pages/agent/AgentReceiptPage';
import AgentLedgerPage from './pages/agent/AgentLedgerPage';
import AgentLedgerListPage from './pages/agent/AgentLedgerListPage';
import AgentSummaryPage from './pages/agent/AgentSummaryPage';
import AgentLeadPage from './pages/agent/AgentLeadPage';
import { loginPathFor } from './utils/loginPath';

// Technical Team Pages (network-hardware-only login: fiber, OLT, ACS, connections roster)
import TechnicalConnectionsPage from './pages/technical/TechnicalConnectionsPage';
import TechnicalComplaintsPage from './pages/technical/TechnicalComplaintsPage';
import TechnicalDashboardPage from './pages/technical/TechnicalDashboardPage';
import TechnicalOltStatusPage from './pages/technical/TechnicalOltStatusPage';
import TechnicalOltDetailPage from './pages/technical/TechnicalOltDetailPage';
import TechnicalPonOverviewPage from './pages/technical/TechnicalPonOverviewPage';
import TechnicalSpeedTestPage from './pages/technical/TechnicalSpeedTestPage';
import TechnicalOnuListPage from './pages/technical/TechnicalOnuListPage';
import TechnicalOnuDetailPage from './pages/technical/TechnicalOnuDetailPage';
import TechnicalAlarmsPage from './pages/technical/TechnicalAlarmsPage';
import TechnicalNetworkMapPage from './pages/technical/TechnicalNetworkMapPage';
import TechnicalAppLayout from './components/technical/TechnicalAppLayout';
import { IS_DEMO, IS_LAXMI_HOST, DEMO_BRAND } from './utils/demo';
import { resolveBrand } from './utils/appBrand';

// `moduleKey`/`action` let a route stay reachable by a non-admin role that's been
// granted that right via the User Rights matrix, even though it fails the `roles` check
// (which was written when these pages were unconditionally admin-only). Routes that must
// never be delegable (Staff Users, User Rights itself, Settings) simply omit the prop.
const ProtectedRoute = ({ children, roles, moduleKey, action = 'view' }) => {
  const { user, loading, hasModuleRight } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  // Mirror api.js's 401 handler: a customer-portal session (anything under /portal, i.e.
  // app.laxmicablevision.in) must bounce to /portal/login, and an agent session (/agent,
  // i.e. agent.laxmicablevision.in) to /agent/login — not the staff /login page, or a
  // sudden logout on those domains shows the crm.laxmicablevision.in login UI.
  if (!user) return <Navigate to={loginPathFor(window.location.pathname)} replace />;
  if (roles && !roles.some(r => hasRole(user, r))) {
    if (!(moduleKey && hasModuleRight(moduleKey, action))) return <Navigate to={homeForRole(user.role)} replace />;
  }
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (user) return <Navigate to={homeForRole(user.role)} replace />;
  return children;
};

// laxmicablevision.in / www.laxmicablevision.in is the public marketing domain — logged-out
// visitors there should land on the marketing page, not get bounced to the staff login form.
// crm.laxmicablevision.in (and any other host, e.g. local dev) keeps the original behavior.
const MARKETING_HOSTS = ['laxmicablevision.in', 'www.laxmicablevision.in'];

// app.laxmicablevision.in is the customer-facing domain — logged-out visitors there
// land on the customer portal login, not the staff login form.
const PORTAL_HOSTS = ['app.laxmicablevision.in'];

// agent.laxmicablevision.in is the collection-agent domain — logged-out visitors there
// land on the agent login. Note /agent/* is deliberately still reachable on
// crm.laxmicablevision.in (no host redirect): the admin "login-as" impersonation flow in
// AgentCollectionPage swaps tokens in the CRM origin's localStorage, which a different
// domain could not read.
const AGENT_HOSTS = ['agent.laxmicablevision.in'];
const CRM_HOSTS = ['crm.laxmicablevision.in'];

// Marks every screen of the public demo copy, so nobody mistakes it for a live system.
const DemoBadge = () => (IS_DEMO ? (
  <div className="demo-badge" style={{ position: 'fixed', top: 'calc(6px + env(safe-area-inset-top, 0px))', left: '50%', transform: 'translateX(-50%)', zIndex: 2000, pointerEvents: 'none', background: '#f59e0b', color: '#1f2937', fontSize: '.68rem', fontWeight: 800, letterSpacing: '.04em', padding: '2px 10px', borderRadius: 999, boxShadow: '0 1px 4px rgba(0,0,0,.2)' }}>
    DEMO · fake data, resets nightly
  </div>
) : null);

// The one front door: every role signs in to `/`. Customers and agents stay on `/` and see their own home
// inside their frame (no hop to /portal or /agent); technicians go on to their area. Anything else under `/`
// bounces back here, so admin pages can never open inside the customer or agent frame. The old addresses
// (/portal, /agent, /technical/*) still work for receipts, payment links, installed apps and push links.
const FrontDoor = ({ role }) => {
  const { pathname } = useLocation();
  if (pathname !== '/') return <Navigate to="/" replace />;
  if (role === 'customer') return <CustomerLayout />;
  if (role === 'agent') return <AgentAppLayout />;
  if (role === 'technical') return <Navigate to="/technical" replace />;
  return <div className="loading-screen">This account has no home screen. Please contact the office.</div>; // not a redirect: /login would bounce a signed-in user straight back here
};

// What the front door's page is for each role (staff and admin go on to their dashboard).
const RoleHome = () => {
  const { user } = useAuth();
  if (user?.role === 'customer') return <CustomerDashboard />;
  if (user?.role === 'agent') return <AgentCustomerListPage />;
  return <Navigate to="/dashboard" replace />;
};

// One component serves both "/" and "/technical": React keeps the same instance when the address
// moves between them, so the App View shell (tab bar, header, theme, kept-alive tab pages) is not
// torn down and rebuilt when you tap Network Overview and come back. A technical login gets its own
// shell and its own view-mode key; admin/staff opening /technical stay in their own shell.
const TECHNICAL_ROLES = ['admin', 'staff', 'co-admin', 'technical'];
const RootRoute = () => {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  const admin = useViewMode();
  const techOwn = useViewMode('technicalViewMode');
  const onTechnical = pathname === '/technical' || pathname.startsWith('/technical/');
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (onTechnical) {
    if (!user) return <Navigate to={loginPathFor(window.location.pathname)} replace />;
    if (!TECHNICAL_ROLES.some(r => hasRole(user, r))) return <Navigate to={homeForRole(user.role)} replace />;
    const isTechnical = user.role === 'technical';
    const { viewMode, toggleViewMode } = isTechnical ? techOwn : admin;
    if (!isTechnical) {
      return viewMode === 'app'
        ? <AdminAppLayout viewMode={viewMode} onToggleViewMode={toggleViewMode} />
        : <AdminLayout viewMode={viewMode} onToggleViewMode={toggleViewMode} />;
    }
    return viewMode === 'app'
      ? <TechnicalAppLayout viewMode={viewMode} onToggleViewMode={toggleViewMode} />
      : <TechnicalLayout viewMode={viewMode} onToggleViewMode={toggleViewMode} />;
  }
  const { viewMode, toggleViewMode } = admin;
  if (!user && MARKETING_HOSTS.includes(window.location.hostname)) return <LandingPage />;
  if (!user && PORTAL_HOSTS.includes(window.location.hostname)) return <Navigate to="/portal/login" replace />;
  if (!user && AGENT_HOSTS.includes(window.location.hostname)) return <Navigate to="/agent/login" replace />;
  if (!user) return <Navigate to="/login" replace />;
  if (!['admin', 'staff', 'co-admin'].includes(user.role)) return <FrontDoor role={user.role} />;
  return viewMode === 'app'
    ? <AdminAppLayout viewMode={viewMode} onToggleViewMode={toggleViewMode} />
    : <AdminLayout viewMode={viewMode} onToggleViewMode={toggleViewMode} />;
};

// On crm. and agent. only, replace the shared browser-tab favicon and "Add to Home Screen"
// manifest with that app's own logo. The agent tab title still comes from the company
// name in Settings. app./www hostnames keep the static index.html branding.
function useAgentBrandTab() {
  useEffect(() => {
    // crm. and agent. show their own fixed app logos (crm-logo.png / agent-logo.png)
    // as the tab icon and Add-to-Home-Screen icon.
    const setIcons = (href) => document.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]')
      .forEach(el => { el.href = href; });
    if (IS_DEMO) { setIcons(DEMO_BRAND.logo); document.title = DEMO_BRAND.name; return; }
    if (!IS_LAXMI_HOST) {
      // An operator's copy (shared address): their own company name and logo, Orvyn until set up.
      const m = document.querySelector('link[rel="manifest"]');
      if (m) m.href = '/api/v1/config/manifest';
      const apply = (identity) => {
        const b = resolveBrand({ isDemo: false, isLaxmi: false, identity });
        document.title = b.name;
        setIcons(b.logo);
        document.querySelectorAll('meta[name="apple-mobile-web-app-title"], meta[name="application-name"], meta[name="description"]')
          .forEach(el => { el.content = b.name; });
      };
      apply(null);
      configAPI.get().then(({ data }) => {
        const d = data?.data;
        const src = d?.catv?.name ? d.catv : d?.broadband?.name ? d.broadband : null;
        apply({ name: src?.name || d?.companyName || '', logo: src?.logo || '' });
      }).catch(() => {});
      return;
    }
    if (CRM_HOSTS.includes(window.location.hostname)) {
      const m = document.querySelector('link[rel="manifest"]');
      if (m) m.href = '/crm-manifest.json';
      setIcons('/crm-logo.png?v=2');
      return;
    }
    if (!AGENT_HOSTS.includes(window.location.hostname)) return;

    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) manifestLink.href = '/api/v1/config/manifest';

    configAPI.get().then(({ data }) => {
      const d = data?.data;
      const identity = d?.catv?.name ? d.catv : d?.broadband?.name ? d.broadband : null;
      if (identity?.name) document.title = identity.name;
    }).catch(() => {});
    setIcons('/agent-logo.png?v=2');
  }, []);
}

function App() {
  useAgentBrandTab();
  return (
    <AuthProvider>
      <Router>
        <DemoBadge />
        {/* Toasts start below the top bar (60px web, 56px app view) instead of covering
            App View, the bell and the profile menu, and each has a close button. */}
        <Toaster position="top-right" containerStyle={{ top: 'calc(72px + env(safe-area-inset-top, 0px))' }} toastOptions={{ duration: 4000, style: { borderRadius: '8px', background: '#1e293b', color: '#f1f5f9' } }}>
          {(t) => (
            <ToastBar toast={t}>
              {({ icon, message }) => (
                <>
                  {icon}
                  {message}
                  {t.type !== 'loading' && (
                    <button
                      type="button"
                      aria-label="Close"
                      onClick={() => toast.dismiss(t.id)}
                      style={{ background: 'none', border: 0, color: 'inherit', opacity: 0.7, cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '2px 4px', marginLeft: 4 }}
                    >
                      ✕
                    </button>
                  )}
                </>
              )}
            </ToastBar>
          )}
        </Toaster>
        <Routes>
          {/* Public */}
          <Route path="/welcome" element={<LandingPage />} />
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/portal/login" element={<PublicRoute><LoginPage variant="customer" /></PublicRoute>} />
          <Route path="/agent/login" element={<PublicRoute><LoginPage variant="agent" /></PublicRoute>} />
          <Route path="/portal/register" element={<NewConnectionRequestPage />} />
          <Route path="/portal/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/portal/refund-policy" element={<RefundPolicyPage />} />
          <Route path="/portal/terms" element={<TermsPage />} />
          <Route path="/pay/:token" element={<PayBillPage />} />
          <Route path="/pay/:token/result" element={<PayBillResultPage />} />

          {/* Admin / Staff Routes */}
          <Route path="/" element={<RootRoute />}>
            <Route index element={<RoleHome />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="insights" element={<ProtectedRoute roles={['admin']}><InsightsPage /></ProtectedRoute>} />
            <Route path="nas/online-subscribers" element={<OnlineSubscribersPage />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="stb-inventory" element={<StbInventoryPage />} />
            <Route path="customers/add" element={<AddCustomerPage />} />
            <Route path="customers/:id" element={<RemountOnRouteParam param="id"><CustomerDetailPage /></RemountOnRouteParam>} />
            <Route path="search-customer" element={<SearchPage />} />
            <Route path="payments" element={<PaymentsPage />} />
            <Route path="payments/online" element={<OnlinePaymentsPage />} />
            <Route path="payments/advance" element={<AdvanceReceiptsPage />} />
            <Route path="payment-screenshots" element={<ProtectedRoute roles={['admin']}><PaymentScreenshotsPage /></ProtectedRoute>} />
            <Route path="renewals" element={<RenewalsPage />} />
            <Route path="complaints" element={<ComplaintsPage />} />
            <Route path="complaints/:id" element={<ComplaintDetailPage />} />
            <Route path="service-requests" element={<ServiceRequestsPage />} />
            <Route path="admin-lookup" element={<AdminLookupPage />} />
            <Route path="connection-requests" element={<ConnectionRequestsPage />} />
            <Route path="reconnection-calls" element={<ReconnectionLeadsPage />} />
            <Route path="discontinued-catv" element={<DiscontinuedCatvPage />} />
            <Route path="devices" element={<DevicesPage />} />
            <Route path="olt" element={<OLTPage />} />
            <Route path="olt-info-v2" element={<ProtectedRoute roles={['admin']}><OltInfoV2Page /></ProtectedRoute>} />
            <Route path="olt-logs" element={<Navigate to="/change-ledger?tab=olt" replace />} />
            <Route path="testing" element={<ProtectedRoute roles={['admin']}><TestingPage /></ProtectedRoute>} />
            <Route path="acs" element={<ProtectedRoute roles={['admin']} moduleKey="acs"><AcsPage /></ProtectedRoute>} />
            <Route path="whatsapp-inbox" element={<ProtectedRoute roles={['admin']} moduleKey="whatsappInbox"><WhatsAppInboxPage /></ProtectedRoute>} />
            <Route path="whatsapp-api" element={<ProtectedRoute roles={['admin']} moduleKey="whatsappGateway"><WhatsAppPage /></ProtectedRoute>} />
            <Route path="kapso" element={<ProtectedRoute roles={['admin']} moduleKey="kapso"><KapsoPage /></ProtectedRoute>} />
            <Route path="fiber-map" element={<FiberMapPage />} />
            <Route path="plans" element={<ProtectedRoute roles={['admin']} moduleKey="plans"><PlansPage /></ProtectedRoute>} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="collection-sheet" element={<ProtectedRoute roles={['admin']} moduleKey="reports"><CollectionSheetPage /></ProtectedRoute>} />
            <Route path="users" element={<ProtectedRoute roles={['admin']}><UsersPage /></ProtectedRoute>} />
            <Route path="site-roles" element={<ProtectedRoute roles={['admin']}><SiteRolesPage /></ProtectedRoute>} />
            <Route path="money" element={<MoneyManagerPage />} />
            <Route path="notes" element={<ProtectedRoute roles={['admin']} moduleKey="notes"><NotesPage /></ProtectedRoute>} />
            <Route path="claude-chat" element={IS_LAXMI_HOST ? <ProtectedRoute roles={['admin']}><ClaudeChatPage /></ProtectedRoute> : <Navigate to="/" replace />} />
            <Route path="gst-billing" element={<ProtectedRoute roles={['admin']}><GstBillingPage /></ProtectedRoute>} />
            <Route path="agent-collection" element={<ProtectedRoute roles={['admin']} moduleKey="agentCollection"><AgentCollectionPage /></ProtectedRoute>} />
            <Route path="agent-collection/:id" element={<ProtectedRoute roles={['admin']} moduleKey="agentCollection"><AgentDetailPage /></ProtectedRoute>} />
            {/* Scan & Collect retired (2026-09-18 user request) — kept as a redirect so old
                links/bookmarks and AddCustomerPage's navigate('/scan') don't 404. */}
            <Route path="scan" element={<Navigate to="/stb-inventory" replace />} />
            <Route path="import" element={<ProtectedRoute roles={['admin']}><ImportPage /></ProtectedRoute>} />
            <Route path="history-import" element={<ProtectedRoute roles={['admin']} moduleKey="historyImport"><HistoryImportPage service="catv" /></ProtectedRoute>} />
            <Route path="history-import-broadband" element={<ProtectedRoute roles={['admin']} moduleKey="historyImport"><HistoryImportPage service="broadband" /></ProtectedRoute>} />
            <Route path="balance-update" element={<ProtectedRoute roles={['admin']}><BalanceUpdatePage /></ProtectedRoute>} />
            <Route path="needs-plan" element={<ProtectedRoute roles={['admin']}><NeedsPlanPage /></ProtectedRoute>} />
            <Route path="export-contacts" element={<ProtectedRoute roles={['admin']}><ExportContactsPage /></ProtectedRoute>} />
            <Route path="sync-import" element={<ProtectedRoute roles={['admin']} moduleKey="syncExpiry"><SyncImportPage service="broadband" /></ProtectedRoute>} />
            <Route path="sync-import-catv" element={<ProtectedRoute roles={['admin']} moduleKey="syncExpiry"><SyncImportPage service="catv" /></ProtectedRoute>} />
            <Route path="sync-import-catv-hardware" element={<ProtectedRoute roles={['admin']} moduleKey="syncHardware"><SyncImportPage service="catvHardware" /></ProtectedRoute>} />
            <Route path="link-customers" element={<ProtectedRoute roles={['admin']} moduleKey="linkCustomers"><CustomerLinkingPage /></ProtectedRoute>} />
            <Route path="settings" element={<ProtectedRoute roles={['admin']}><SettingsPage /></ProtectedRoute>} />
            <Route path="imbs" element={<ProtectedRoute roles={['admin']} moduleKey="imbs"><IMBSPage /></ProtectedRoute>} />
            <Route element={<GtplShell />}>
            <Route path="gtpl" element={<ProtectedRoute roles={['admin']} moduleKey="gtplSaathi"><GtplHomePage /></ProtectedRoute>} />
            <Route path="gtpl-saathi-ticket" element={<ProtectedRoute roles={['admin']} moduleKey="gtplSaathi"><GtplSaathiTicketPage /></ProtectedRoute>} />
            <Route path="gtpl-saathi-subscriber" element={<ProtectedRoute roles={['admin']} moduleKey="gtplSaathi"><GtplSaathiSubscriberPage /></ProtectedRoute>} />
            <Route path="gtpl-saathi-multi-renew" element={<ProtectedRoute roles={['admin']} moduleKey="gtplSaathi"><GtplSaathiMultiRenewPage /></ProtectedRoute>} />
            <Route path="gtpl-saathi-modify" element={<ProtectedRoute roles={['admin']} moduleKey="gtplSaathi"><GtplSaathiModifyPage /></ProtectedRoute>} />
            <Route path="gtpl-saathi-hardware-replacement" element={<ProtectedRoute roles={['admin']} moduleKey="gtplSaathi"><GtplSaathiHardwareReplacementPage /></ProtectedRoute>} />
            <Route path="gtpl-vs-crm" element={<ProtectedRoute roles={['admin']} moduleKey="gtplSaathi"><GtplVsCrmPage /></ProtectedRoute>} />
            <Route path="gtpl-expiry-check" element={<ProtectedRoute roles={['admin']} moduleKey="gtplSaathi"><GtplExpiryCheckPage /></ProtectedRoute>} />
            <Route path="gtpl-box-stock" element={<ProtectedRoute roles={['admin']} moduleKey="gtplSaathi"><GtplBoxStockPage /></ProtectedRoute>} />
            <Route path="gtpl-not-in-crm" element={<ProtectedRoute roles={['admin']} moduleKey="gtplSaathi"><GtplOrphanAccountsPage /></ProtectedRoute>} />
            <Route path="gtpl-stb-mismatch" element={<ProtectedRoute roles={['admin']} moduleKey="gtplSaathi"><GtplStbMismatchPage /></ProtectedRoute>} />
            <Route path="gtpl-90-days" element={<ProtectedRoute roles={['admin']} moduleKey="gtplSaathi"><Gtpl90DaysPage /></ProtectedRoute>} />
            <Route path="gtpl-alacarte-bulk" element={<ProtectedRoute roles={['admin']} moduleKey="gtplSaathi"><GtplAlacarteBulkPage /></ProtectedRoute>} />
            <Route path="gtpl-bulk-change-order" element={<ProtectedRoute roles={['admin']} moduleKey="gtplSaathi" action="placeOrder"><GtplBulkChangeOrderPage /></ProtectedRoute>} />
            <Route path="gtpl-saathi-settings" element={<ProtectedRoute roles={['admin']} moduleKey="gtplSaathi"><GtplSaathiSettingsPage /></ProtectedRoute>} />
            <Route path="gtpl-saathi-retry-queue" element={<ProtectedRoute roles={['admin']} moduleKey="gtplSaathi"><GtplRetryQueuePage /></ProtectedRoute>} />
            <Route path="gtpl-saathi-logs" element={<ProtectedRoute roles={['admin']} moduleKey="gtplSaathi"><GtplActionLogPage /></ProtectedRoute>} />
            </Route>
            <Route path="messaging" element={<MessageCenterPage />} />
            <Route path="messaging/api" element={<ProtectedRoute roles={['admin']}><MessagingApiPage /></ProtectedRoute>} />
            <Route path="change-ledger" element={<ProtectedRoute roles={['admin']} moduleKey="changeLedger"><ChangeLedgerPage /></ProtectedRoute>} />
            <Route path="recycle-bin" element={<ProtectedRoute roles={['admin']} moduleKey="recycleBin"><RecycleBinPage /></ProtectedRoute>} />
            <Route path="ads" element={<ProtectedRoute roles={['admin']}><AdsPage /></ProtectedRoute>} />
          </Route>

          {/* Agent App Routes (mobile, cable-TV-only collection interface) */}
          <Route path="/agent" element={<ProtectedRoute roles={['agent']}><AgentAppLayout /></ProtectedRoute>}>
            <Route index element={<AgentCustomerListPage />} />
            <Route path="customer/:id" element={<AgentCustomerDetailPage />} />
            <Route path="customer/:id/collect" element={<AgentCollectPaymentPage />} />
            <Route path="customer/:id/collect-balance" element={<AgentCollectBalancePage />} />
            <Route path="customer/:id/ledger" element={<AgentLedgerPage />} />
            <Route path="receipt/:paymentId" element={<AgentReceiptPage />} />
            <Route path="ledger" element={<AgentLedgerListPage />} />
            <Route path="collections" element={<AgentSummaryPage />} />
            <Route path="lead" element={<AgentLeadPage />} />
          </Route>

          {/* Technical Team Routes (network-hardware-only login: no billing/customer-management access).
              Also reachable by admin/staff via the "Technical Details" sidebar link (navConfig.js) —
              mirrors the backend's technicalAccess middleware, which already allows admin/staff into
              every /api/v1/technical/* endpoint these pages call. */}
          <Route path="/technical" element={<RootRoute />}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<TechnicalDashboardPage />} />
            <Route path="olt-status" element={<TechnicalOltStatusPage />} />
            <Route path="olt-status/detail" element={<TechnicalOltDetailPage />} />
            <Route path="pon-overview" element={<TechnicalPonOverviewPage />} />
            <Route path="onus" element={<TechnicalOnuListPage />} />
            <Route path="onus/:slot/:port/:onuId" element={<TechnicalOnuDetailPage />} />
            <Route path="alarms" element={<TechnicalAlarmsPage />} />
            <Route path="network-map" element={<TechnicalNetworkMapPage />} />
            <Route path="connections" element={<TechnicalConnectionsPage />} />
            <Route path="complaints" element={<TechnicalComplaintsPage />} />
            <Route path="fiber-map" element={<FiberMapPage />} />
            <Route path="olt" element={<OLTPage />} />
            <Route path="acs" element={<AcsPage />} />
            <Route path="speedtest" element={<TechnicalSpeedTestPage />} />
          </Route>

          {/* Customer Portal Routes */}
          <Route path="/portal" element={<ProtectedRoute roles={['customer']}><CustomerLayout /></ProtectedRoute>}>
            <Route index element={<CustomerDashboard />} />
            <Route path="profile" element={<CustomerProfile />} />
            <Route path="complaints" element={<CustomerComplaints />} />
            <Route path="requests" element={<CustomerRequests />} />
            <Route path="notifications" element={<CustomerNotifications />} />
            <Route path="refer" element={<CustomerRefer />} />
            <Route path="payments" element={<CustomerPayments />} />
            <Route path="plans" element={<CustomerPlansPage />} />
            <Route path="renew" element={<CustomerRenewPage />} />
            <Route path="wifi" element={<CustomerWifiSettings />} />
            <Route path="usage" element={<CustomerUsageDetails />} />
            <Route path="speedtest" element={<CustomerSpeedTest />} />
          </Route>

          <Route path="*" element={<Navigate to={loginPathFor(window.location.pathname)} replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
